import { API_BASE_URL } from '@/config';
import {
  claimScopedAttempt,
  classifyMissingProfile,
  withAbortSignal,
} from '@/lib/authBootstrap/index';
import { useAuthStore } from '@/stores/authStore';
import { useAuth, useSession, useUser } from '@clerk/clerk-react';
import type { UserRole } from '@ergshoot/shared';
import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Hook to sync Clerk user data with our backend and local authStore.
 *
 * Clerk handles authentication, but our D1 database holds the actual
 * user profile including role. This hook:
 * 1. Waits for Clerk to be loaded and user to be signed in
 * 2. Fetches user profile from our /auth/me endpoint using Clerk's getToken
 * 3. Syncs that data to the local authStore for components like Sidebar
 *
 * Returns loading/error states so App.tsx can show appropriate UI.
 */
export function useUserSync() {
  const { isLoaded: clerkLoaded, isSignedIn, user: clerkUser } = useUser();
  const { session, isLoaded: sessionLoaded } = useSession();
  const { getToken } = useAuth();
  const setAuth = useAuthStore((state) => state.setAuth);
  const clearAuth = useAuthStore((state) => state.clearAuth);
  const storeUser = useAuthStore((state) => state.user);

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userRole, setUserRole] = useState<UserRole | null>(null);
  const lastSessionIdRef = useRef<string | null>(null);
  const attemptedSessionIdRef = useRef<string | null>(null);
  const activeRequestRef = useRef<AbortController | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const syncUser = useCallback(
    async (sessionId: string) => {
      if (mountedRef.current) {
        setIsLoading(true);
        setError(null);
      }

      activeRequestRef.current?.abort();
      const controller = new AbortController();
      activeRequestRef.current = controller;
      let timedOut = false;
      const timeoutId = setTimeout(() => {
        timedOut = true;
        controller.abort();
      }, 10000);

      try {
        // Get fresh token from Clerk using the hook
        const token = await withAbortSignal(getToken(), controller.signal);

        if (!token) {
          if (!mountedRef.current) return;
          setError('Unable to get authentication token');
          setUserRole(null);
          return;
        }

        // Make API call with the token and abort signal
        const response = await fetch(`${API_BASE_URL}/auth/me`, {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          signal: controller.signal,
        });

        clearTimeout(timeoutId);

        // A missing D1 profile is registration for a new Clerk user, but a
        // recovery state for an account that was previously provisioned.
        if (response.status === 404) {
          const missingProfileOutcome = classifyMissingProfile({
            clerkRole: clerkUser?.publicMetadata?.role,
          });
          const terminalOutcome =
            missingProfileOutcome === 'account-missing' ? 'ACCOUNT_NOT_FOUND' : 'PROFILE_NOT_FOUND';
          if (
            !mountedRef.current ||
            controller.signal.aborted ||
            activeRequestRef.current !== controller
          )
            return;

          console.warn('User not found in database - Clerk user exists but no D1 record');
          setError(terminalOutcome);
          setUserRole(null);
          clearAuth();
          return;
        }

        if (
          !mountedRef.current ||
          controller.signal.aborted ||
          activeRequestRef.current !== controller
        )
          return;

        if (response.status === 401) return;

        // Handle other non-OK responses
        if (!response.ok) {
          const errorText = await response.text().catch(() => 'Unknown error');
          console.error(`Auth API error ${response.status}:`, errorText);
          // FEAT-MAY30-001: surface a clear suspended-account message instead
          // of a generic "authentication failed (403)".
          if (response.status === 403 && errorText.includes('ACCOUNT_SUSPENDED')) {
            setError('ACCOUNT_SUSPENDED');
          } else {
            setError(`Authentication failed (${response.status})`);
          }
          setUserRole(null);
          return;
        }

        const data = await response.json();

        if (data.success && data.data) {
          const userData = data.data;

          // Update authStore with user data (including profiles for multi-role support
          // and FEAT-APR08-001 freemium subscription state)
          setAuth(
            {
              id: userData.id,
              email: userData.email,
              name: userData.name,
              username: userData.username,
              role: userData.role as UserRole,
              profiles: userData.profiles || [],
              active_profile: userData.active_profile || null,
              active_profile_id: userData.active_profile?.id || null,
              subscription_status: userData.subscription_status,
              subscription_source: userData.subscription_source,
              billing_state: userData.billing_state,
              is_frozen: userData.is_frozen,
            },
            '', // No access token needed - Clerk handles auth
            '' // No refresh token needed - Clerk handles auth
          );

          setUserRole(userData.role as UserRole);
          lastSessionIdRef.current = sessionId;
        } else {
          // User exists in Clerk but not in our DB - might need role selection
          setError(data.error || 'User not found in database');
          setUserRole(null);
        }
      } catch (err) {
        clearTimeout(timeoutId);

        if (!mountedRef.current) return;

        // Handle abort/timeout specifically
        if (controller.signal.aborted && activeRequestRef.current !== controller) {
          return;
        }

        if (err instanceof Error && err.name === 'AbortError' && timedOut) {
          console.error('User sync timed out after 10 seconds');
          setError('Request timed out. Please refresh the page.');
          setUserRole(null);
        } else {
          console.error('Failed to sync user:', err);
          setError(err instanceof Error ? err.message : 'Failed to fetch user profile');
          setUserRole(null);
        }
      } finally {
        if (mountedRef.current && activeRequestRef.current === controller) {
          activeRequestRef.current = null;
          setIsLoading(false);
        }
      }
    },
    [setAuth, getToken, clearAuth, clerkUser?.publicMetadata?.role]
  );

  const resync = useCallback(() => {
    const currentSessionId = session?.id;
    if (!currentSessionId) {
      setError('Unable to resolve the authenticated session. Please sign out and try again.');
      setIsLoading(false);
      return;
    }

    // Claim this session before starting the request. A Clerk metadata refresh
    // can recreate syncUser and rerun the bootstrap effect while this explicit
    // resync is in flight; keeping the claim prevents a duplicate request.
    attemptedSessionIdRef.current = currentSessionId;
    lastSessionIdRef.current = null;
    syncUser(currentSessionId);
  }, [session?.id, syncUser]);

  useEffect(() => {
    // Wait for Clerk to be ready
    if (!clerkLoaded) {
      return;
    }

    // If not signed in, clear auth and stop loading
    if (!isSignedIn) {
      activeRequestRef.current?.abort();
      activeRequestRef.current = null;
      clearAuth();
      setIsLoading(false);
      setUserRole(null);
      lastSessionIdRef.current = null;
      attemptedSessionIdRef.current = null;
      return;
    }

    // Check if we already fetched for this session
    if (!sessionLoaded) {
      return;
    }

    const currentSessionId = session?.id || null;
    if (!currentSessionId) {
      setError('Unable to resolve the authenticated session. Please sign out and try again.');
      setIsLoading(false);
      return;
    }
    if (lastSessionIdRef.current === currentSessionId && storeUser) {
      // Already synced for this session
      setUserRole(storeUser.role);
      setIsLoading(false);
      return;
    }

    const attempt = claimScopedAttempt(attemptedSessionIdRef.current, currentSessionId);
    attemptedSessionIdRef.current = attempt.attemptedScopeId;
    if (!attempt.shouldAttempt) {
      // An explicit resync may already own this session. Do not expose a
      // completed state until that in-flight request settles.
      if (!activeRequestRef.current) setIsLoading(false);
      return;
    }

    // Fetch user profile from our API
    syncUser(currentSessionId);
  }, [clerkLoaded, isSignedIn, sessionLoaded, session?.id, clearAuth, syncUser, storeUser]);

  return {
    isLoading,
    isClerkLoaded: clerkLoaded,
    isSignedIn,
    userRole,
    error,
    user: storeUser,
    resync,
  };
}
